import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI, projectsAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { 
  FolderOpen, CheckSquare, Users, Clock, AlertTriangle, ArrowRight,
  MapPin, Plus, DollarSign, TrendingUp,
} from 'lucide-react';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];
const STATUS_LABELS = { planning: 'Planning', in_progress: 'In Progress', completed: 'Completed', on_hold: 'On Hold', nearing_completion: 'Nearing Completion' };
const PRIORITY_COLORS = { high: '#ef4444', medium: '#f59e0b', low: '#6b7280' };
const fmt = (n) => typeof n === 'number' ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '₹0';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border rounded px-3 py-2 shadow-lg text-sm">
      <p className="font-medium">{label || payload[0]?.name}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="text-xs">{p.name || p.dataKey}: {typeof p.value === 'number' && p.value > 1000 ? fmt(p.value) : p.value}</p>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const { user, isAdmin, isClient } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentProjects, setRecentProjects] = useState([]);
  const [upcomingTasks, setUpcomingTasks] = useState([]);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isClient) { navigate('/client-dashboard'); return; }
    fetchData();
  }, [isClient, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, projectsRes] = await Promise.all([
        dashboardAPI.getStats(),
        projectsAPI.list(),
      ]);
      setStats(statsRes.data);
      setRecentProjects(projectsRes.data.slice(0, 5));
      setUpcomingTasks(statsRes.data.upcoming_tasks || []);
      setCharts(statsRes.data.charts || {});
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'status-completed';
      case 'in_progress': return 'status-in_progress';
      case 'nearing_completion': return 'status-nearing_completion';
      case 'on_hold': return 'status-on_hold';
      default: return 'status-planning';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'priority-high';
      case 'medium': return 'priority-medium';
      default: return 'priority-low';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="bento-grid">{[...Array(4)].map((_, i) => <Card key={`sk-${i}`} className="animate-pulse"><CardContent className="p-6"><div className="h-20 bg-muted rounded" /></CardContent></Card>)}</div>
        </div>
      </Layout>
    );
  }

  const projectStatus = (charts?.project_status || []).map(s => ({ ...s, name: STATUS_LABELS[s.name] || s.name }));
  const taskPriority = (charts?.task_priority || []).map(p => ({ ...p, fill: PRIORITY_COLORS[p.name] || '#6b7280' }));
  const monthlyRevenue = (charts?.monthly_revenue || []).map(m => ({ ...m, month: m.month?.substring(5) || m.month }));
  const budgetVsActual = charts?.budget_vs_actual || [];
  const ffePipeline = charts?.ffe_pipeline || [];

  return (
    <Layout>
      <div className="space-y-6 sm:space-y-8" data-testid="dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-light tracking-tight">Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">Welcome back, {user?.name?.split(' ')[0]}</p>
          </div>
          <Button onClick={() => navigate('/projects/new')} className="btn-primary" data-testid="new-project-btn">
            <Plus className="mr-2 h-4 w-4" />New Project
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="bento-grid" data-testid="stats-grid">
          <Card className="card-hover cursor-pointer border-l-4 border-l-blue-500" onClick={() => navigate('/projects')} data-testid="projects-tile">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Total Projects</p>
                  <p className="text-2xl sm:text-3xl font-light mt-2">{stats?.projects?.total || 0}</p>
                </div>
                <div className="p-3 rounded-xl bg-blue-500/10 text-blue-500"><FolderOpen className="h-5 w-5" /></div>
              </div>
              <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-chart-2" />{stats?.projects?.active || 0} Active</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-chart-3" />{stats?.projects?.completed || 0} Done</span>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover cursor-pointer border-l-4 border-l-emerald-500" onClick={() => navigate('/tasks')} data-testid="tasks-tile">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Tasks</p>
                  <p className="text-2xl sm:text-3xl font-light mt-2">{stats?.tasks?.total || 0}</p>
                </div>
                <div className="p-3 rounded-xl bg-emerald-500/10 text-emerald-500"><CheckSquare className="h-5 w-5" /></div>
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm mb-2"><span className="text-muted-foreground">Completion</span><span className="font-medium">{stats?.tasks?.completion_rate || 0}%</span></div>
                <Progress value={stats?.tasks?.completion_rate || 0} className="h-1" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover cursor-pointer border-l-4 border-l-amber-500" onClick={() => navigate('/invoices')} data-testid="revenue-tile">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Revenue</p>
                  <p className="text-2xl sm:text-3xl font-light mt-2">{fmt(stats?.invoices?.total_revenue)}</p>
                </div>
                <div className="p-3 rounded-xl bg-amber-500/10 text-amber-500"><DollarSign className="h-5 w-5" /></div>
              </div>
              <p className="text-sm text-muted-foreground mt-4">{fmt(stats?.invoices?.total_outstanding)} outstanding</p>
            </CardContent>
          </Card>

          <Card className="card-hover cursor-pointer border-l-4 border-l-rose-500" onClick={() => navigate('/tasks?status=overdue')} data-testid="overdue-tile">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Overdue</p>
                  <p className="text-2xl sm:text-3xl font-light mt-2">{stats?.tasks?.overdue || 0}</p>
                </div>
                <div className="p-3 rounded-xl bg-rose-500/10 text-rose-500"><AlertTriangle className="h-5 w-5" /></div>
              </div>
              <p className="text-sm text-muted-foreground mt-4">{stats?.tasks?.pending || 0} tasks pending</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Project Status Pie */}
          {projectStatus.length > 0 && (
            <Card data-testid="chart-project-status">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Project Status</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={projectStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} innerRadius={40} paddingAngle={2}>
                      {projectStatus.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Task Priority Donut */}
          {taskPriority.length > 0 && (
            <Card data-testid="chart-task-priority">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Open Tasks by Priority</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={taskPriority} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} innerRadius={40} paddingAngle={2}>
                      {taskPriority.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Monthly Revenue Bar */}
          {monthlyRevenue.length > 0 && (
            <Card data-testid="chart-monthly-revenue">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={monthlyRevenue}>
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="revenue" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Budget vs Actual */}
          {budgetVsActual.length > 0 && (
            <Card className="md:col-span-2" data-testid="chart-budget-actual">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Budget vs Actual by Project</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={budgetVsActual}>
                    <XAxis dataKey="project" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
                    <Bar dataKey="estimated" fill="#f59e0b" name="Estimated" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="actual" fill="#3b82f6" name="Actual" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* FF&E Pipeline */}
          {ffePipeline.length > 0 && (
            <Card data-testid="chart-ffe-pipeline">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">FF&E Pipeline</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={ffePipeline} layout="vertical">
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={70} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" fill="#10b981" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Two Column: Projects & Tasks */}
        <div className="grid lg:grid-cols-2 gap-4 sm:gap-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2 px-4 sm:px-6">
              <CardTitle className="text-base sm:text-lg font-medium">Recent Projects</CardTitle>
              <Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate('/projects')} data-testid="view-all-projects-btn">View all<ArrowRight className="ml-1 h-3 w-3" /></Button>
            </CardHeader>
            <CardContent className="pt-0">
              {recentProjects.length === 0 ? (
                <div className="empty-state py-8"><FolderOpen className="h-10 w-10 text-muted-foreground/50 mb-3" /><p className="text-sm text-muted-foreground">No projects yet</p></div>
              ) : (
                <div className="space-y-3">
                  {recentProjects.map((project) => (
                    <Link key={project.project_id} to={`/projects/${project.project_id}`} className="flex items-center justify-between p-3 border border-border hover:bg-muted/50 transition-colors" data-testid={`project-item-${project.project_id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{project.name}</p>
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" />{project.city}</p>
                      </div>
                      <span className={`status-badge ${getStatusColor(project.status)}`}>{project.status.replace('_', ' ')}</span>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2 px-4 sm:px-6">
              <CardTitle className="text-base sm:text-lg font-medium">Upcoming Tasks</CardTitle>
              <Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate('/tasks')} data-testid="view-all-tasks-btn">View all<ArrowRight className="ml-1 h-3 w-3" /></Button>
            </CardHeader>
            <CardContent className="pt-0">
              {upcomingTasks.length === 0 ? (
                <div className="empty-state py-8"><CheckSquare className="h-10 w-10 text-muted-foreground/50 mb-3" /><p className="text-sm text-muted-foreground">No upcoming tasks</p></div>
              ) : (
                <div className="space-y-3">
                  {upcomingTasks.map((task) => (
                    <div key={task.task_id} className="task-card cursor-pointer" onClick={() => navigate(`/tasks?task=${task.task_id}`)} data-testid={`task-item-${task.task_id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{task.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">{task.project_name || 'Unknown Project'}</p>
                          <span className={`status-badge ${getPriorityColor(task.priority)}`}>{task.priority}</span>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground"><Clock className="h-3 w-3" />{formatDate(task.due_date)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Cities */}
        {stats?.cities?.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-lg font-medium">Projects by City</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {stats.cities.map((city) => (
                  <div key={city.city} className="p-4 border border-border text-center hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => navigate(`/projects?city=${city.city}`)} data-testid={`city-${city.city}`}>
                    <MapPin className="h-5 w-5 mx-auto text-muted-foreground mb-2" />
                    <p className="text-lg font-light">{city.count}</p>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1 truncate">{city.city}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
